import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, deleteDoc, getFirestore, getDoc } from 'firebase/firestore';
import { InvoiceService } from '../services/InvoiceService';
import EmailService from '../services/EmailService';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { getAuth } from 'firebase/auth';

const DocumentsList = ({ documents, type, onClose, onCreateNew, surveys }) => {
  const modalContentRef = useRef(null);
  const navigate = useNavigate();

  const [selectedDocs, setSelectedDocs] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [sortBy, setSortBy] = useState('date'); // Default sort by date
  const [sortDirection, setSortDirection] = useState('desc'); // Default newest first
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [activeTab, setActiveTab] = useState('invoices'); // For invoice mode with surveys
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(null);
  const [isHoveringMarkPaid, setIsHoveringMarkPaid] = useState(null);
  const [sendingReminderId, setSendingReminderId] = useState(null);

  // Close the modal if user clicks outside `modalContentRef`
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        modalContentRef.current && 
        !modalContentRef.current.contains(e.target)
      ) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Toggle the selection of one document
  const toggleDocSelection = (e, docId) => {
    e.stopPropagation();
    setSelectedDocs(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };

  // ==============================
  //    PROCESS / FORMAT DOCS
  // ==============================
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const d = new Date(dateString);
      return isNaN(d.getTime())
        ? null
        : d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
    } catch {
      return null;
    }
  };

  const extractClientName = (agreementName) => {
    if (!agreementName) return 'Client';
    const match = agreementName.match(/Agreement with\s+([^-]+)/i);
    return match && match[1] ? match[1].trim() : 'Client';
  };

  const processedDocuments = (documents || []).map(docItem => {
    const processed = { ...docItem };

    if (type === 'invoices') {
      processed.title = processed.gig || processed.title || processed.project || 'Client Photography';
      processed.client = processed.customer || processed.client || processed.clientName || 'Client';
      processed.clientEmail = processed.customerEmail || processed.email || '';
      processed.amount = processed.price || processed.amount || 0;
      processed.currency = processed.currency || 'USD';
      processed.status = processed.status || 'pending';
      
      // Format the sent date if available
      processed.formattedSentDate = processed.sentDate ? formatDate(processed.sentDate) : null;
      
      // Format the due date
      processed.formattedDueDate = formatDate(processed.dueDate) || formatDate(processed.due_date) || formatDate(processed.created);
      
      // Format createdAt if available
      if (processed.createdAt) {
        let createdDate;
        if (processed.createdAt.toDate) {
          createdDate = processed.createdAt.toDate();
        } else if (processed.createdAt.seconds) {
          createdDate = new Date(processed.createdAt.seconds * 1000);
        } else {
          createdDate = new Date(processed.createdAt);
        }
        processed.formattedCreatedDate = formatDate(createdDate);
      }
      
      // For display, prioritize created date, then sent date, then due date
      processed.formattedDate = processed.formattedCreatedDate || processed.formattedSentDate || processed.formattedDueDate;
      
      // Store the original date for sorting - prioritize createdAt for consistent ordering
      // Handle both Firestore Timestamp objects and regular date values
      processed.sortDate = processed.createdAt ? 
                           (processed.createdAt.toDate ? new Date(processed.createdAt.toDate()).getTime() : 
                            processed.createdAt.seconds ? new Date(processed.createdAt.seconds * 1000).getTime() :
                            new Date(processed.createdAt).getTime()) :
                           processed.created ? new Date(processed.created).getTime() :
                           processed.sentDate ? new Date(processed.sentDate).getTime() :
                           processed.dueDate ? new Date(processed.dueDate).getTime() : 
                           processed.due_date ? new Date(processed.due_date).getTime() : 0;
    } else if (type === 'agreements') {
      processed.title = processed.name || processed.title || 'Agreement';
      processed.client = processed.clientName || processed.client || extractClientName(processed.title);
      // Read price from multiple possible fields
      processed.amount = processed.price || processed.amount || 0;
      processed.currency = processed.currency || 'USD';
      processed.formattedDate = (
        formatDate(processed.date) ||
        formatDate(processed.created) ||
        formatDate(processed.createdAt)
      );
      
      // Store the original date for sorting - handle Firestore timestamps
      processed.sortDate = processed.createdAt ? 
                           (processed.createdAt.toDate ? new Date(processed.createdAt.toDate()).getTime() : 
                            processed.createdAt.seconds ? new Date(processed.createdAt.seconds * 1000).getTime() :
                            new Date(processed.createdAt).getTime()) :
                           processed.date ? new Date(processed.date).getTime() : 
                           processed.created ? new Date(processed.created).getTime() : 0;
      
      // Use status from document if it exists
      const docStatus = (processed.status || '').toLowerCase();
      
      // Check for signed status first (highest priority)
      if (
        processed.freelancerSignature ||
        processed.clientSignature ||
        processed.signed === true ||
        processed.signed === 'true' ||
        docStatus === 'signed'
      ) {
        processed.status = 'Signed';
      } 
      // Check for sent status
      else if (docStatus === 'sent') {
        processed.status = 'Sent';
      }
      // Check for unsigned status
      else if (
        processed.signed === false ||
        processed.signed === 'false' ||
        docStatus === 'unsigned'
      ) {
        processed.status = 'Unsigned';
      }
      // If the agreement has text, mark it as 'Created'
      else if (processed.text && processed.text.length > 0) {
        processed.status = 'Created';
      }
      // Default status
      else {
        processed.status = 'Draft';
      }
    } else if (type === 'surveys') {
      processed.title = processed.title || 'Feedback Survey';
      processed.client = `${processed.responseCount || 0} response${processed.responseCount !== 1 ? 's' : ''}`;
      processed.amount = '';
      processed.formattedDate = formatDate(processed.created) || formatDate(processed.updated);
      
      // Store the original date for sorting
      processed.sortDate = processed.created ? new Date(processed.created).getTime() : 
                           processed.updated ? new Date(processed.updated).getTime() : 0;
      
      // Survey status
      processed.status = processed.isActive ? 'Active' : 'Inactive';
    }
    // For projects or other doc types, adapt as needed
    return processed;
  });

  // ==============================
  //        SORT DOCUMENTS
  // ==============================
  const sortedDocuments = [...processedDocuments].sort((a, b) => {
    // Sort by date
    if (sortBy === 'date') {
      const aValue = a.sortDate || 0;
      const bValue = b.sortDate || 0;
      return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
    }
    
    // Sort by status
    if (sortBy === 'status') {
      // For invoices: paid > pending > other
      if (type === 'invoices') {
        const getStatusValue = (status) => {
          const s = (status || '').toLowerCase();
          if (s === 'paid') return 3;
          if (s === 'pending') return 2;
          return 1;
        };
        
        const aValue = getStatusValue(a.status);
        const bValue = getStatusValue(b.status);
        return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
      }
      
      // For agreements: signed > unsigned > draft
      if (type === 'agreements') {
        const getStatusValue = (status) => {
          const s = (status || '').toLowerCase();
          if (s === 'signed') return 3;
          if (s === 'unsigned') return 2;
          return 1;
        };
        
        const aValue = getStatusValue(a.status);
        const bValue = getStatusValue(b.status);
        return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
      }
    }
    
    // Sort by amount (for invoices and agreements)
    if (sortBy === 'amount') {
      const aValue = parseFloat(a.amount) || 0;
      const bValue = parseFloat(b.amount) || 0;
      return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
    }
    
    // Default sort
    return 0;
  });

  // ==============================
  //   SELECT / DESELECT ALL DOCS
  // ==============================
  const selectedCount = Object.values(selectedDocs).filter(Boolean).length;
  const toggleSelectAll = () => {
    const allSelectedNow = selectedCount === processedDocuments.length;
    if (allSelectedNow) {
      setSelectedDocs({});
    } else {
      const newSelections = {};
      processedDocuments.forEach(d => {
        if (d.id) newSelections[d.id] = true;
      });
      setSelectedDocs(newSelections);
    }
  };

  // ==============================
  //     DELETE SELECTED DOCS
  // ==============================
  const deleteSelectedDocuments = async () => {
    setIsDeleting(true);
    const firestore = getFirestore();
    const collection = 
      type === 'invoices' ? 'invoices' :
      type === 'agreements' ? 'agreements' : 
      type === 'surveys' ? 'surveys' :
      'projects'; 

    try {
      const deletePromises = Object.entries(selectedDocs)
        .filter(([, isSelected]) => isSelected)
        .map(([docId]) => 
          deleteDoc(doc(firestore, collection, docId))
        );
      await Promise.all(deletePromises);

      // Reset state, but do NOT close the entire modal
      setSelectedDocs({});
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting documents:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // ==============================
  //        STATUS STYLING
  // ==============================
  const getStatusClasses = (status = '') => {
    const s = status.toLowerCase();
    if (['paid', 'completed', 'signed'].includes(s)) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
    } else if (['sent'].includes(s)) {
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
    } else if (['pending', 'in progress', 'draft', 'unsigned', 'created'].includes(s)) {
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
    } else {
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
    }
  };

  // ==============================
  //        VIEW DOCUMENT
  // ==============================
  const handleViewDocument = (documentId) => {
    if (type === 'agreements') {
      navigate(`/agreement/${documentId}`);
    } else if (type === 'invoices') {
      navigate(`/preview-invoice/${documentId}`);
    } else if (type === 'surveys') {
      navigate(`/survey/${documentId}/responses`);
    }
    // Possibly others
    onClose(); // closes the main modal if user just wants to go see doc
  };

  // ==============================
  //    HANDLE SORT SELECTION
  // ==============================
  const handleSortChange = (option) => {
    if (sortBy === option) {
      // Toggle direction if clicking the same option
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort option with default direction
      setSortBy(option);
      setSortDirection('desc');
    }
    setShowSortOptions(false);
  };

  // Handle sending invoice reminder
  const handleSendReminder = async (invoiceId) => {
    if (sendingReminderId) return; // Prevent multiple simultaneous sends
    
    setSendingReminderId(invoiceId);
    
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        toast.error('Please sign in to send reminders');
        return;
      }
      
      // Fetch the full invoice data from Firestore
      const db = getFirestore();
      const invoiceRef = doc(db, 'invoices', invoiceId);
      const invoiceDoc = await getDoc(invoiceRef);
      
      if (!invoiceDoc.exists()) {
        toast.error('Invoice not found');
        return;
      }
      
      const invoiceData = {
        id: invoiceId,
        ...invoiceDoc.data()
      };
      
      // Check if invoice has customer email
      if (!invoiceData.email && !invoiceData.clientEmail) {
        toast.error('No customer email found for this invoice');
        return;
      }
      
      // Get user's email accounts
      const userAccounts = await EmailService.getAccounts(currentUser.uid);
      
      if (!userAccounts || !userAccounts.accounts || userAccounts.accounts.length === 0) {
        toast.error('Please connect an email account first. Go to Settings > Email Accounts.');
        return;
      }
      
      // Send the reminder
      await EmailService.sendInvoiceReminder(invoiceData, userAccounts.accounts);
      
      toast.success('Reminder email sent successfully!');
      
      // Optional: Update invoice to track when reminder was sent
      // This could be useful for future features like tracking reminder history
      
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.message || 'Failed to send reminder email');
    } finally {
      setSendingReminderId(null);
    }
  };

  // Handle marking invoice as paid manually
  const handleMarkAsPaid = async (invoiceId, paymentMethod = 'Cash') => {
    setMarkingPaidId(invoiceId);
    try {
      await InvoiceService.markAsPaidManually(invoiceId, paymentMethod);
      toast.success(`Invoice marked as paid (${paymentMethod})`);
      setShowMarkPaidModal(null);
      
      // Optimistically update the status in the UI immediately
      // The parent component will handle proper data refresh through other means
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to mark invoice as paid');
    } finally {
      setMarkingPaidId(null);
    }
  };

  // ==============================
  //      RENDER SURVEY RESULTS  
  // ==============================
  const renderSurveys = () => {
    if (!surveys || surveys.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="mb-6 mx-auto w-16 h-16 bg-gray-100 dark:bg-[#1A1A1A] rounded-2xl flex items-center justify-center">
            <svg className="h-8 w-8 text-gray-400 dark:text-[#666666]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2 text-gray-900 dark:text-white">No Survey Results</h3>
          <p className="text-gray-600 dark:text-[#666666]">Survey responses from your invoices will appear here.</p>
        </div>
      );
    }

    return surveys.map((survey, idx) => (
      <div 
        key={survey.id || idx}
        className="group bg-white dark:bg-[#1A1A1A] rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-[#1F1F1F] transition-all duration-200 hover:shadow-md border border-gray-200 dark:border-[#333] shadow-sm"
      >
        <div className="flex flex-col md:flex-row justify-between items-start gap-3 md:gap-4">
          {/* Left side */}
          <div className="flex items-start gap-3 w-full md:w-auto">
            <div className="mt-0.5 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-md flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-gray-900 dark:text-white text-base font-semibold tracking-tight mb-1 truncate">
                {survey.title || 'Client Feedback Survey'}
              </h3>
              <p className="text-gray-500 dark:text-[#999999] text-sm">
                {survey.responseCount || 0} response{survey.responseCount !== 1 ? 's' : ''} collected
              </p>
              {survey.linkedInvoice && (
                <p className="text-gray-400 dark:text-[#666666] text-xs mt-1">
                  From invoice: {survey.linkedInvoice}
                </p>
              )}
            </div>
          </div>
          {/* Right side */}
          <div className="flex flex-col md:items-end w-full md:w-auto">
            <div className="text-gray-900 dark:text-white text-sm font-semibold tracking-tight mb-1">
              {survey.questions?.length || 0} question{survey.questions?.length !== 1 ? 's' : ''}
            </div>
            {survey.created && (
              <div className="text-gray-500 dark:text-[#999999] text-xs mb-2">
                {formatDate(survey.created)}
              </div>
            )}
            <span 
              className={`inline-block px-2 py-1 rounded-md text-xs font-medium ${
                (survey.responseCount || 0) > 0
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
              }`}
            >
              {(survey.responseCount || 0) > 0 ? 'Has Responses' : 'No Responses'}
            </span>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button 
            onClick={() => handleViewDocument(survey.id)} 
            className="text-xs text-[#FFB27D] font-semibold hover:text-[#FFA164] transition-colors duration-200 px-3 py-1.5 rounded-md hover:bg-[#FFB27D]/8 flex items-center gap-1.5"
          >
            View Results
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    ));
  };

  // ==============================
  //       RENDER DOC CARDS
  // ==============================
  const renderDocuments = () => {
    if (type === 'invoices') {
      return sortedDocuments.map((invoice, idx) => (
        <div 
          key={invoice.id || idx}
          className={`group bg-white dark:bg-[#1A1A1A] rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-[#1F1F1F] transition-all duration-200 hover:shadow-md
            ${selectedDocs[invoice.id] 
              ? 'border border-[#FFB27D] shadow-sm shadow-[#FFB27D]/5' 
              : 'border border-gray-200 dark:border-[#333] shadow-sm'}`}
        >
          <div className="relative">
            {/* Left side: Title + client - constrained width */}
            <div className="flex items-start gap-3 pr-[150px]">
              {invoice.id && (
                <div 
                  className={`mt-0.5 w-4 h-4 rounded-md flex items-center justify-center border cursor-pointer transition-all duration-200 flex-shrink-0
                    ${selectedDocs[invoice.id]
                      ? 'bg-[#FFB27D] border-[#FFB27D]'
                      : 'border-gray-300 dark:border-gray-600 group-hover:border-gray-400 dark:group-hover:border-gray-500'}`}
                  onClick={(e) => toggleDocSelection(e, invoice.id)}
                >
                  {selectedDocs[invoice.id] && (
                    <svg 
                      className="w-2.5 h-2.5 text-black"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        d="M5 13l4 4L19 7" 
                      />
                    </svg>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="relative mb-1">
                  <h3 className="text-gray-900 dark:text-white text-base font-semibold tracking-tight overflow-hidden whitespace-nowrap">
                    {invoice.title}
                  </h3>
                  {invoice.title && invoice.title.length > 35 && (
                    <div className="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-white group-hover:from-gray-50 dark:from-[#1A1A1A] dark:group-hover:from-[#1F1F1F] to-transparent pointer-events-none transition-all duration-200"></div>
                  )}
                </div>
                <div className="relative">
                  <p className="text-gray-500 dark:text-[#999999] text-sm overflow-hidden whitespace-nowrap">
                    {invoice.clientEmail || invoice.client}
                  </p>
                  {(invoice.clientEmail || invoice.client) && (invoice.clientEmail || invoice.client).length > 40 && (
                    <div className="absolute top-0 right-0 w-6 h-full bg-gradient-to-l from-white group-hover:from-gray-50 dark:from-[#1A1A1A] dark:group-hover:from-[#1F1F1F] to-transparent pointer-events-none transition-all duration-200"></div>
                  )}
                </div>
              </div>
            </div>
            {/* Right side: Amount + date + status - ABSOLUTE POSITIONED */}
            <div className="absolute top-0 right-0 flex flex-col items-end">
              <div className="text-gray-900 dark:text-white text-lg font-bold tracking-tight mb-1 whitespace-nowrap">
                {invoice.currency} {invoice.amount}
              </div>
              {invoice.formattedSentDate && (
                <div className="text-gray-500 dark:text-[#999999] text-xs mb-1 whitespace-nowrap">
                  Sent: {invoice.formattedSentDate}
                </div>
              )}
              {invoice.formattedDueDate && (
                <div className="text-gray-500 dark:text-[#999999] text-xs mb-2 whitespace-nowrap">
                  Due: {invoice.formattedDueDate}
                </div>
              )}
              <span
                className={`inline-block px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getStatusClasses(invoice.status)}`}
              >
                {invoice.status}
              </span>
            </div>
          </div>
          <div className="mt-4 flex justify-between items-center">
            {/* Action buttons for unpaid invoices */}
            <div className="flex gap-2">
              {/* Edit Button - Only show for unpaid invoices */}
              {invoice.status?.toLowerCase() !== 'paid' && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/edit-invoice/${invoice.id}`);
                    onClose();
                  }}
                  className="group flex items-center justify-center bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-md transition-colors duration-200 px-2 py-1.5"
                  title="Edit invoice"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="ml-1.5 text-xs font-medium">Edit</span>
                </button>
              )}
              
              {/* Send Reminder Button - Only show for unpaid invoices */}
              {invoice.status?.toLowerCase() !== 'paid' && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendReminder(invoice.id);
                  }}
                  disabled={sendingReminderId === invoice.id}
                  className="group flex items-center justify-center bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md transition-colors duration-200 px-2 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={sendingReminderId === invoice.id ? 'Sending reminder...' : 'Send reminder email'}
                >
                  {sendingReminderId === invoice.id ? (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  )}
                  <span className="ml-1.5 text-xs font-medium">
                    {sendingReminderId === invoice.id ? 'Sending...' : 'Remind'}
                  </span>
                </button>
              )}
              
              {/* Mark as Paid Icon - Only show for unpaid invoices */}
              {invoice.status?.toLowerCase() !== 'paid' && (
              <motion.button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMarkPaidModal(invoice.id);
                }}
                onMouseEnter={() => setIsHoveringMarkPaid(invoice.id)}
                onMouseLeave={() => setIsHoveringMarkPaid(null)}
                whileTap={{ scale: 0.95 }}
                disabled={markingPaidId === invoice.id}
                animate={{
                  width: isHoveringMarkPaid === invoice.id ? 'auto' : '28px',
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="group flex items-center justify-center overflow-hidden bg-gray-100 hover:bg-green-50 dark:bg-gray-800 dark:hover:bg-green-900/20 text-gray-600 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 rounded-md transition-colors duration-200 h-7"
                title={markingPaidId === invoice.id ? 'Marking as paid...' : 'Mark as paid'}
              >
                <motion.div
                  className="flex items-center gap-1.5 px-1.5"
                  animate={{
                    paddingRight: isHoveringMarkPaid === invoice.id ? '6px' : '0px',
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  {markingPaidId === invoice.id ? (
                    <svg className="w-3.5 h-3.5 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <motion.span
                    className="text-xs font-medium whitespace-nowrap"
                    animate={{
                      opacity: isHoveringMarkPaid === invoice.id ? 1 : 0,
                      width: isHoveringMarkPaid === invoice.id ? 'auto' : 0,
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    {markingPaidId === invoice.id ? 'Marking...' : 'Mark as Paid'}
                  </motion.span>
                </motion.div>
              </motion.button>
              )}
            </div>
            
            <button 
              onClick={() => handleViewDocument(invoice.id)} 
              className="text-xs text-[#FFB27D] font-semibold hover:text-[#FFA164] transition-colors duration-200 px-3 py-1.5 rounded-md hover:bg-[#FFB27D]/8 flex items-center gap-1.5"
            >
              View Details
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      ));
    } 
    else if (type === 'agreements') {
      return sortedDocuments.map((agreement, idx) => (
        <div 
          key={agreement.id || idx}
          className={`group bg-gray-50 dark:bg-[#1A1A1A] rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-[#252525] transition-all duration-200 shadow-sm hover:shadow-md
            ${selectedDocs[agreement.id] ? 'border border-[#FFB27D]' : 'border border-gray-200 dark:border-[#252525]'}`}
        >
          <div className="relative">
            {/* Left side - constrained width */}
            <div className="flex items-start gap-3 pr-[150px]">
              {agreement.id && (
                <div 
                  className={`mt-0.5 w-4 h-4 rounded-md flex items-center justify-center border cursor-pointer transition-all duration-200 flex-shrink-0
                    ${selectedDocs[agreement.id]
                      ? 'bg-[#FFB27D] border-[#FFB27D]'
                      : 'border-gray-300 dark:border-gray-600 group-hover:border-gray-400 dark:group-hover:border-gray-500'}`}
                  onClick={(e) => toggleDocSelection(e, agreement.id)}
                >
                  {selectedDocs[agreement.id] && (
                    <svg 
                      className="w-2.5 h-2.5 text-black" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        d="M5 13l4 4L19 7" 
                      />
                    </svg>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="relative mb-1">
                  <h3 className="text-gray-900 dark:text-white text-base font-semibold tracking-tight overflow-hidden whitespace-nowrap">{agreement.title}</h3>
                  {agreement.title && agreement.title.length > 35 && (
                    <div className="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-gray-50 group-hover:from-gray-100 dark:from-[#1A1A1A] dark:group-hover:from-[#252525] to-transparent pointer-events-none transition-all duration-200"></div>
                  )}
                </div>
                <div className="relative">
                  <p className="text-gray-500 dark:text-[#999999] text-sm overflow-hidden whitespace-nowrap">{agreement.client}</p>
                  {agreement.client && agreement.client.length > 40 && (
                    <div className="absolute top-0 right-0 w-6 h-full bg-gradient-to-l from-gray-50 group-hover:from-gray-100 dark:from-[#1A1A1A] dark:group-hover:from-[#252525] to-transparent pointer-events-none transition-all duration-200"></div>
                  )}
                </div>
                {agreement.status?.toLowerCase() === 'unsigned' && (
                  <p className="text-red-500 mt-1 text-xs font-medium flex items-center gap-1">
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="truncate">Pending signature</span>
                  </p>
                )}
                <button 
                  onClick={() => handleViewDocument(agreement.id)} 
                  className="mt-2 text-xs text-[#FFB27D] font-semibold hover:text-[#FFA164] transition-colors duration-200 px-3 py-1.5 rounded-md hover:bg-[#FFB27D]/8 flex items-center gap-1.5 w-fit"
                >
                  View Details
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Right side - ABSOLUTE POSITIONED */}
            <div className="absolute top-0 right-0 flex flex-col items-end">
              {agreement.amount && parseFloat(agreement.amount) > 0 && (
                <div className="text-gray-900 dark:text-white text-base font-semibold tracking-tight mb-1 whitespace-nowrap">
                  {agreement.currency || 'USD'} {parseFloat(agreement.amount).toLocaleString()}
                </div>
              )}
              {agreement.formattedDate && (
                <div className="text-gray-500 dark:text-[#999999] text-xs mb-2 whitespace-nowrap">
                  {agreement.formattedDate}
                </div>
              )}
              <span 
                className={`inline-block px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getStatusClasses(agreement.status)}`}
              >
                {agreement.status}
              </span>
            </div>
          </div>
        </div>
      ));
    } 
    else if (type === 'surveys') {
      return sortedDocuments.map((survey, idx) => (
        <div 
          key={survey.id || idx}
          className={`group bg-white dark:bg-[#1A1A1A] rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-[#1F1F1F] transition-all duration-200 hover:shadow-md
            ${selectedDocs[survey.id] ? 'border border-[#FFB27D]' : 'border border-gray-200 dark:border-[#252525]'}`}
        >
          <div className="relative">
            {/* Left side - constrained width */}
            <div className="flex items-start gap-3 pr-[150px]">
              {survey.id && (
                <div 
                  className={`mt-0.5 w-4 h-4 rounded-md flex items-center justify-center border cursor-pointer transition-all duration-200 flex-shrink-0
                    ${selectedDocs[survey.id]
                      ? 'bg-[#FFB27D] border-[#FFB27D]'
                      : 'border-gray-300 dark:border-gray-600 group-hover:border-gray-400 dark:group-hover:border-gray-500'}`}
                  onClick={(e) => toggleDocSelection(e, survey.id)}
                >
                  {selectedDocs[survey.id] && (
                    <svg 
                      className="w-2.5 h-2.5 text-black" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        d="M5 13l4 4L19 7" 
                      />
                    </svg>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="relative mb-1">
                  <h3 className="text-gray-900 dark:text-white text-base font-semibold tracking-tight overflow-hidden whitespace-nowrap">{survey.title}</h3>
                  {survey.title && survey.title.length > 35 && (
                    <div className="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-white group-hover:from-gray-50 dark:from-[#1A1A1A] dark:group-hover:from-[#1F1F1F] to-transparent pointer-events-none transition-all duration-200"></div>
                  )}
                </div>
                <div className="relative">
                  <p className="text-gray-500 dark:text-[#999999] text-sm overflow-hidden whitespace-nowrap">{survey.client}</p>
                  {survey.client && survey.client.length > 40 && (
                    <div className="absolute top-0 right-0 w-6 h-full bg-gradient-to-l from-white group-hover:from-gray-50 dark:from-[#1A1A1A] dark:group-hover:from-[#1F1F1F] to-transparent pointer-events-none transition-all duration-200"></div>
                  )}
                </div>
                {survey.description && (
                  <p className="text-gray-400 dark:text-[#666666] text-xs mt-1 line-clamp-2">{survey.description}</p>
                )}
              </div>
            </div>
            {/* Right side - ABSOLUTE POSITIONED */}
            <div className="absolute top-0 right-0 flex flex-col items-end">
              <div className="text-gray-900 dark:text-white text-sm font-semibold tracking-tight mb-1 whitespace-nowrap">
                {survey.questions?.length || 0} question{survey.questions?.length !== 1 ? 's' : ''}
              </div>
              {survey.formattedDate && (
                <div className="text-gray-500 dark:text-[#999999] text-xs mb-2 whitespace-nowrap">
                  {survey.formattedDate}
                </div>
              )}
              <span 
                className={`inline-block px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getStatusClasses(survey.status)}`}
              >
                {survey.status}
              </span>
            </div>
          </div>
          <div className="mt-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {survey.allowAnonymous ? 'Anonymous' : 'Named'} responses
              </span>
              {survey.sendReminder && (
                <span className="text-xs text-blue-600 dark:text-blue-400">
                  • Reminders enabled
                </span>
              )}
            </div>
            <button 
              onClick={() => handleViewDocument(survey.id)} 
              className="text-xs text-[#FFB27D] font-semibold hover:text-[#FFA164] transition-colors duration-200 px-3 py-1.5 rounded-md hover:bg-[#FFB27D]/8 flex items-center gap-1.5"
            >
              View Responses
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      ));
    }
    else {
      // For 'projects' or other doc types
      return sortedDocuments.map((p, idx) => (
        <div 
          key={p.id || idx}
          className={`group bg-gray-50 dark:bg-[#1A1A1A] rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-[#252525] transition-all duration-200 shadow-sm hover:shadow-md
            ${selectedDocs[p.id] ? 'border border-[#FFB27D]' : 'border border-gray-200 dark:border-[#252525]'}`}
        >
          <div className="relative">
            <div className="flex items-start gap-3 pr-[150px]">
              {p.id && (
                <div 
                  className={`mt-0.5 w-4 h-4 rounded-md flex items-center justify-center border cursor-pointer transition-all duration-200 flex-shrink-0
                    ${selectedDocs[p.id]
                      ? 'bg-[#FFB27D] border-[#FFB27D]'
                      : 'border-gray-300 dark:border-gray-600 group-hover:border-gray-400 dark:group-hover:border-gray-500'}`}
                  onClick={(e) => toggleDocSelection(e, p.id)}
                >
                  {selectedDocs[p.id] && (
                    <svg 
                      className="w-2.5 h-2.5 text-black" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        d="M5 13l4 4L19 7" 
                      />
                    </svg>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="relative mb-1">
                  <h3 className="text-gray-900 dark:text-white text-base font-semibold tracking-tight overflow-hidden whitespace-nowrap">
                    {p.name || p.projectName || 'Project'}
                  </h3>
                  {(p.name || p.projectName || 'Project').length > 35 && (
                    <div className="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-gray-50 group-hover:from-gray-100 dark:from-[#1A1A1A] dark:group-hover:from-[#252525] to-transparent pointer-events-none transition-all duration-200"></div>
                  )}
                </div>
                <div className="relative">
                  <p className="text-gray-500 dark:text-[#999999] text-sm overflow-hidden whitespace-nowrap">{p.client}</p>
                  {p.client && p.client.length > 40 && (
                    <div className="absolute top-0 right-0 w-6 h-full bg-gradient-to-l from-gray-50 group-hover:from-gray-100 dark:from-[#1A1A1A] dark:group-hover:from-[#252525] to-transparent pointer-events-none transition-all duration-200"></div>
                  )}
                </div>
              </div>
            </div>
            <div className="absolute top-0 right-0 flex flex-col items-end">
              {p.startDate && (
                <div className="text-gray-500 dark:text-[#999999] text-xs mb-2 whitespace-nowrap">
                  Started: {formatDate(p.startDate)}
                </div>
              )}
              <span 
                className={`inline-block px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getStatusClasses(p.status)}`}
              >
                {p.status || 'In Progress'}
              </span>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button 
              onClick={() => handleViewDocument(p.id)} 
              className="text-xs text-[#FFB27D] font-semibold hover:text-[#FFA164] transition-colors duration-200 px-3 py-1.5 rounded-md hover:bg-[#FFB27D]/8 flex items-center gap-1.5"
            >
              View Details
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      ));
    }
  };

  // ==============================
  // RENDER COMPONENT
  // ==============================
  // If no documents at all
  if (!documents || documents.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div 
          ref={modalContentRef}
          className="bg-[#111111] border-2 border-[#1A1A1A] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-xl"
        >
          <div className="flex items-center justify-between p-4 md:p-6 border-b-2 border-[#1A1A1A]">
            <div className="flex items-center gap-4">
              <h2 className="text-lg md:text-xl font-medium text-white tracking-tight">
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </h2>
              <button
                onClick={onCreateNew}
                className="text-sm font-medium text-white bg-[#1A1A1A] hover:bg-[#FFB27D]/10 px-3 md:px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-300"
              >
                <svg 
                  className="h-4 w-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M12 4v16m8-8H4" 
                  />
                </svg>
                <span className="hidden md:inline">New {type === 'invoices' ? 'Invoice' : 'Agreement'}</span>
              </button>
            </div>
            <button onClick={onClose} className="text-[#666666] hover:text-white transition-colors p-2 hover:bg-[#252525] rounded-lg">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </button>
          </div>
          <div className="p-6 md:p-12 text-center">
            <div className="mb-6 mx-auto w-16 md:w-20 h-16 md:h-20 bg-[#1A1A1A] rounded-2xl flex items-center justify-center">
              <svg 
                className="h-8 md:h-10 w-8 md:w-10 text-[#666666]" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                />
              </svg>
            </div>
            <h3 className="text-xl md:text-2xl font-medium mb-3 text-white tracking-tight">
              No {type} found
            </h3>
            <p className="text-[#AAAAAA] text-base md:text-lg mb-6">
              You haven&apos;t created any {type} yet.
            </p>
            <button
              onClick={onCreateNew}
              className="text-base font-medium text-white bg-[#FFB27D] hover:bg-[#FFB27D]/90 px-6 py-3 rounded-xl transition-all duration-300 shadow-lg"
            >
              Create Your First {type === 'invoices' ? 'Invoice' : type === 'agreements' ? 'Agreement' : type === 'surveys' ? 'Survey' : 'Document'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If we DO have docs
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        ref={modalContentRef}
        className="relative bg-white dark:bg-[#111111] border-2 border-gray-200 dark:border-[#1A1A1A] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-xl"
      >
        {/* Top Bar */}
        <div className="border-b border-gray-200 dark:border-[#252525]">
          {/* Main Header */}
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center gap-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </h2>
              
              {/* Show tabs for invoices (always show surveys tab) */}
              {type === 'invoices' && (
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#1A1A1A] p-1 rounded-lg">
                  <button
                    onClick={() => setActiveTab('invoices')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                      activeTab === 'invoices'
                        ? 'bg-white dark:bg-[#2A2A2A] text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-[#999999] hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    Invoices ({documents?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('surveys')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                      activeTab === 'surveys'
                        ? 'bg-white dark:bg-[#2A2A2A] text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-[#999999] hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    Survey Results ({surveys?.length || 0})
                  </button>
                </div>
              )}
            </div>

            {/* Close Button - Always visible and prominent */}
            <button 
              onClick={onClose}
              className="text-gray-500 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white transition-colors p-2 hover:bg-gray-100 dark:hover:bg-[#252525] rounded-lg"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Secondary Header with Actions */}
          <div className="flex items-center justify-between px-6 pb-4">
            {/* Left side - Sort */}
            <div className="flex items-center gap-3">
              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortOptions(!showSortOptions)}
                  className="text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
                >
                  <svg 
                    className="h-4 w-4" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1.5} 
                      d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" 
                    />
                  </svg>
                  <span>Sort</span>
                  {sortDirection === 'desc' ? 
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg> :
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  }
                </button>
                
                {showSortOptions && (
                  <div className="absolute left-0 mt-2 z-20 bg-white dark:bg-[#1A1A1A] border-2 border-gray-200 dark:border-[#252525] rounded-xl shadow-lg p-2 min-w-[160px]">
                    <button
                      onClick={() => handleSortChange('date')}
                      className={`w-full text-left px-4 py-2 text-sm font-medium rounded-lg ${
                        sortBy === 'date' 
                          ? 'bg-[#FFB27D]/10 text-[#FFB27D]' 
                          : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#252525]'
                      }`}
                    >
                      By Date {sortBy === 'date' && (sortDirection === 'desc' ? '(Newest)' : '(Oldest)')}
                    </button>
                    
                    <button
                      onClick={() => handleSortChange('amount')}
                      className={`w-full text-left px-4 py-2 text-sm font-medium rounded-lg ${
                        sortBy === 'amount' 
                          ? 'bg-[#FFB27D]/10 text-[#FFB27D]' 
                          : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#252525]'
                      }`}
                    >
                      By Amount {sortBy === 'amount' && (sortDirection === 'desc' ? '(Highest)' : '(Lowest)')}
                    </button>
                    
                    <button
                      onClick={() => handleSortChange('status')}
                      className={`w-full text-left px-4 py-2 text-sm font-medium rounded-lg ${
                        sortBy === 'status' 
                          ? 'bg-[#FFB27D]/10 text-[#FFB27D]' 
                          : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#252525]'
                      }`}
                    >
                      By Status {sortBy === 'status' && type === 'invoices' && (sortDirection === 'desc' ? '(Paid first)' : '(Unpaid first)')}
                      {sortBy === 'status' && type === 'agreements' && (sortDirection === 'desc' ? '(Signed first)' : '(Unsigned first)')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right side - Actions */}
            <div className="hidden md:flex items-center gap-3">
              {/* Only show create button for invoices tab, not surveys tab */}
              {!(type === 'invoices' && activeTab === 'surveys') && (
                <button
                  onClick={onCreateNew}
                  className="text-sm font-medium text-white bg-[#FFB27D] hover:bg-[#FFA164] px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>New {type === 'invoices' ? 'Invoice' : type === 'agreements' ? 'Agreement' : 'Document'}</span>
                </button>
              )}

              <button
                onClick={toggleSelectAll}
                className="text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] hover:bg-gray-50 dark:hover:bg-[#252525] px-4 py-2.5 rounded-lg transition-all duration-200"
              >
                {selectedCount === processedDocuments.length && processedDocuments.length > 0 ? 'Deselect All' : 'Select All'}
              </button>

              {selectedCount > 0 && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-sm font-medium text-white bg-red-600/20 hover:bg-red-600/30 px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Delete ({selectedCount})</span>
                </button>
              )}
            </div>

            {/* Mobile Actions Button */}
            <button
              onClick={() => setShowMobileActions(!showMobileActions)}
              className="md:hidden text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#2A2A2A] p-2 rounded-lg border border-gray-200 dark:border-[#333]"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Actions Menu */}
        {showMobileActions && (
          <div className="md:hidden absolute top-[72px] right-4 bg-[#1A1A1A] rounded-xl shadow-lg border-2 border-[#252525] p-2 z-20">
            {/* Only show create button for invoices tab, not surveys tab */}
            {!(type === 'invoices' && activeTab === 'surveys') && (
              <button
                onClick={() => {
                  onCreateNew();
                  setShowMobileActions(false);
                }}
                className="w-full text-left text-sm font-medium text-white hover:bg-[#252525] px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M12 4v16m8-8H4" 
                  />
                </svg>
                <span>New {type === 'invoices' ? 'Invoice' : type === 'agreements' ? 'Agreement' : 'Document'}</span>
              </button>
            )}
            
            <button
              onClick={() => {
                toggleSelectAll();
                setShowMobileActions(false);
              }}
              className="w-full text-left text-sm font-medium text-white hover:bg-[#252525] px-4 py-2 rounded-lg transition-all duration-300"
            >
              {selectedCount === processedDocuments.length && processedDocuments.length > 0 
                ? 'Deselect All' 
                : 'Select All'}
            </button>

            {selectedCount > 0 && (
              <button
                onClick={() => {
                  setShowDeleteConfirm(true);
                  setShowMobileActions(false);
                }}
                className="w-full text-left text-sm font-medium text-red-400 hover:bg-[#252525] px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                  />
                </svg>
                <span>Delete ({selectedCount})</span>
              </button>
            )}
            
            {/* Add sort options to mobile menu */}
            <div className="w-full border-t border-[#333] my-2 pt-2">
              <div className="px-4 py-1 text-[#999999] text-xs">Sort by:</div>
              <button
                onClick={() => {
                  handleSortChange('date');
                  setShowMobileActions(false);
                }}
                className={`w-full text-left text-sm font-medium px-4 py-2 rounded-lg ${
                  sortBy === 'date' 
                  ? 'text-[#FFB27D]' 
                  : 'text-white hover:bg-[#252525]'
                }`}
              >
                Date {sortBy === 'date' && (sortDirection === 'desc' ? '↓' : '↑')}
              </button>
              <button
                onClick={() => {
                  handleSortChange('amount');
                  setShowMobileActions(false);
                }}
                className={`w-full text-left text-sm font-medium px-4 py-2 rounded-lg ${
                  sortBy === 'amount' 
                  ? 'text-[#FFB27D]' 
                  : 'text-white hover:bg-[#252525]'
                }`}
              >
                Amount {sortBy === 'amount' && (sortDirection === 'desc' ? '↓' : '↑')}
              </button>
              <button
                onClick={() => {
                  handleSortChange('status');
                  setShowMobileActions(false);
                }}
                className={`w-full text-left text-sm font-medium px-4 py-2 rounded-lg ${
                  sortBy === 'status' 
                  ? 'text-[#FFB27D]' 
                  : 'text-white hover:bg-[#252525]'
                }`}
              >
                Status {sortBy === 'status' && (sortDirection === 'desc' ? '↓' : '↑')}
              </button>
            </div>
          </div>
        )}

        {/* Document Cards */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] space-y-4">
          {type === 'invoices' && activeTab === 'surveys' 
            ? renderSurveys() 
            : renderDocuments()}
        </div>

        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-10">
            <div className="bg-white dark:bg-[#1A1A1A] border-2 border-gray-200 dark:border-[#333] rounded-xl p-6 md:p-8 max-w-md w-full shadow-xl">
              <h3 className="text-xl md:text-2xl font-medium text-gray-900 dark:text-white mb-4 tracking-tight">
                Delete {selectedCount} {type}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-base md:text-lg mb-6 md:mb-8">
                Are you sure you want to delete {selectedCount === 1 ? 'this' : 'these'} {selectedCount} {selectedCount === 1 ? type.slice(0, -1) : type}? This action cannot be undone.
              </p>
              <div className="flex gap-4 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-6 py-3 rounded-xl bg-gray-100 dark:bg-[#333] text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-[#444] transition-all duration-300 text-base font-medium"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={deleteSelectedDocuments}
                  className="px-6 py-3 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-all duration-300 flex items-center gap-2 text-base font-medium"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mark as Paid Modal */}
        {showMarkPaidModal && (
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-10">
            <div className="bg-white dark:bg-[#1A1A1A] border-2 border-gray-200 dark:border-[#333] rounded-xl p-6 md:p-8 max-w-md w-full shadow-xl">
              <h3 className="text-xl md:text-2xl font-medium text-gray-900 dark:text-white mb-4 tracking-tight">
                Mark Invoice as Paid
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-base mb-6">
                Select the payment method used for this invoice:
              </p>
              
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => handleMarkAsPaid(showMarkPaidModal, 'Cash')}
                  disabled={markingPaidId === showMarkPaidModal}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
                >
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">Cash Payment</p>
                    <p className="text-xs text-gray-500 dark:text-[#666666]">Paid in cash</p>
                  </div>
                </button>
                
                <button
                  onClick={() => handleMarkAsPaid(showMarkPaidModal, 'Check')}
                  disabled={markingPaidId === showMarkPaidModal}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
                >
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">Check Payment</p>
                    <p className="text-xs text-gray-500 dark:text-[#666666]">Paid by check</p>
                  </div>
                </button>
                
                <button
                  onClick={() => handleMarkAsPaid(showMarkPaidModal, 'Bank Transfer')}
                  disabled={markingPaidId === showMarkPaidModal}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
                >
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">Bank Transfer</p>
                    <p className="text-xs text-gray-500 dark:text-[#666666]">Direct bank transfer</p>
                  </div>
                </button>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowMarkPaidModal(null)}
                  disabled={markingPaidId === showMarkPaidModal}
                  className="flex-1 px-4 py-2 border border-gray-200 dark:border-[#333] text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsList;